import React from 'react';

/**
 * A reusable Select (dropdown) component.
 *
 * Props:
 * - label: (Optional) Text for the <label> element.
 * - id: (Optional) The id for the select element, used for the label's htmlFor.
 * - name: (Optional) The name attribute for the select element.
 * - value: The currently selected value.
 * - onChange: Function to call when the selection changes.
 * - options: An array of objects, e.g., [{ value: 'v1', label: 'Label 1' }, { value: 'v2', label: 'Label 2' }]
 * - leftIcon: (Optional) A React icon component (like lucide-react) to display inside the select box on the left.
 * - className: (Optional) Additional classes to apply to the main wrapper div.
 * - props: Any other props to pass directly to the <select> element (e.g., disabled).
 */
const Select = ({ 
    label, 
    id, 
    name, 
    value, 
    onChange, 
    options = [], 
    leftIcon, 
    className = '',
    ...props 
}) => {
    const selectId = id || name;

    return (
        <div className={`w-full ${className}`}>
            {label && (
                <label 
                    htmlFor={selectId} 
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                </label>
            )}
            <div className="relative">
                {leftIcon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {leftIcon}
                    </div>
                )}
                <select
                    id={selectId}
                    name={name}
                    value={value}
                    onChange={onChange}
                    className={`
                        block w-full h-10 px-3 pr-10 border border-gray-300 rounded-md shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        sm:text-sm appearance-none bg-white
                        ${leftIcon ? 'pl-10' : 'pl-3'}
                    `}
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem',
                    }}
                    {...props}
                >
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default Select;